import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Dropdown, Menu, Popover, message } from 'antd';
import {
	fetchAllPartsForSupplier,
	getPartialPartsUploadBulkStatus,
	partsRestCrudThunksForSupplier,
	uploadPartialPartsBulk,
} from '../../thunks/parts_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import PartRowDisplay from '../part_row_display/PartRowDisplay';
import { PARTS_CRUD_ACTION_CREATORS } from '../../actions/parts_actions';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { DEFAULT_NAME_SORTER, FILTER_CONFIG_NAMES, ROLE_TYPES } from '../../utils/DataConstants';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import {
	PART_MASTER_FLATFILE_HEADER,
	getFlatfileRecordForPart,
} from '../stock_locations_detail_details_page/stock_location_constants';
import moment from 'moment';
import { DATE_FORMAT } from '../../utils/DataFormatterUtils';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import {
	changeFilterValueToArrayOfIds,
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { getIsActiveFilterField } from '../../utils/FilterUtils';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import PartialPartsUpload from './PartialPartsUpload';
import OWPoll from '../common/OWPoll';
import { isGLCodeAllowedBySettings } from '../../utils/InventoryUtils';
import TagCreation from '../TagCreation';
import { TagEntityType } from '../TagSystem';
import { tagsOnPartsRestCrudThunksForSupplier } from '../../thunks/tags_thunks';
import { Select } from 'antd';
import TagDisplay from '../TagDisplay';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';

require('./PartsIndexPage.less');

const PARTS_TC = 'partsIndex';

const getMissngPartNumbers = (existing, newList) =>
	existing
		.filter(
			(part) =>
				!newList.find(
					(record) =>
						`${part.id}` === `${record.id}` ||
						nullSafeGetOrElse('partNumber', part, -1) === record.partNumber
				)
		)
		.map((_) => nullSafeGetOrElse('partNumber', _, nullSafeGet('id', _)));

const PartsIndexPage: FC<any> = ({
	history,
	updatePartsFilters,
	clearAndUpdateFilters,
	parts,
	fetchParts,
	fetchAllParts,
	createBulk,
	currentUser,
	vendors,
	fetchVendors,
	fetchMultipleVendors,
	tags,
	fetchTags,
	fetchMultipleTags,
	uploadPartialBulk,
	getBulkUploadStatus,
	companyConfig,
}) => {
	const [downloading, setDownloading] = useState(false);
	const [exportingCsv, setExportingCsv] = useState(false);
	const [partsForUpload, setPartsForUpload] = useState([]);
	const [uploadPopupVisible, setUploadPopupVisible] = useState(false);
	const [allVendors, setAllVendors] = useState<any[]>([]);
	const [bulkUploadInProgress, setBulkUploadInProgress] = useState(false);

	const [partialUploadPopupVisible, setPartialUploadPopupVisible] = useState(false);
	const [bulkUploadInitiated, setBulkUploadInitiated] = useState(false);

	const _hideUploadPopup = useCallback(() => setUploadPopupVisible(false), []);
	const _hidePartialUploadPopup = () => setPartialUploadPopupVisible(false);

	const userCurrency = useMemo(() => getCurrency({ currentUser }).id, [currentUser]);

	const onRow = useCallback(
		(record) => ({
			onClick: () => history.push(`/supplier/parts/detail/${record.id}`),
		}),
		[history]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				sorter: true,
				render: (_, record) => <PartRowDisplay part={record} refreshParts={refreshParts} />,
			},
		],
		[]
	);

	const filterConfig = useMemo(
		() => [
			getIsActiveFilterField(updatePartsFilters, PARTS_TC, true),
			{
				label: 'Default Vendor',
				fieldName: FILTER_CONFIG_NAMES.DEFAULT_VENDORS,
				mode: 'multiple',
				stateSlice: vendors,
				targetCollectionName: PARTS_TC,
				fetch: fetchVendors,
				fetchMultiple: fetchMultipleVendors,
				filtersValueAccessor: (filtersStateSlice) => {
					const ids = nullSafeGet(
						`${PARTS_TC}.filters.${FILTER_CONFIG_NAMES.DEFAULT_VENDORS}`,
						filtersStateSlice
					);
					return changeFilterValueToArrayOfIds(ids);
				},
				keyAccessor: (vendor) => vendor.id,
				valueAccessor: (vendor) => vendor.id,
				handleChange: (ids) => {
					updatePartsFilters(
						{
							[FILTER_CONFIG_NAMES.DEFAULT_VENDORS]: ids.join(',') || undefined,
						},
						PARTS_TC
					);
				},
			},
			{
				label: 'Tags',
				fieldName: FILTER_CONFIG_NAMES.TAGS,
				mode: 'multiple',
				stateSlice: tags,
				targetCollectionName: PARTS_TC,
				fetch: fetchTags,
				fetchMultiple: fetchMultipleTags,
				filtersValueAccessor: (filtersStateSlice) => {
					const ids = nullSafeGet(
						`${PARTS_TC}.filters.${FILTER_CONFIG_NAMES.TAGS}`,
						filtersStateSlice
					);
					return changeFilterValueToArrayOfIds(ids);
				},
				keyAccessor: (tag) => tag.id,
				valueAccessor: (tag) => tag.id,
				labelAccessor: (tag) => <TagDisplay tag={tag} />,
				renderItem: (tag) => (
					<Select.Option key={tag.id} value={tag.id}>
						<TagDisplay tag={tag} />
					</Select.Option>
				),
				renderSelectedText: (tag) => tag.label,
				handleChange: (ids) => {
					updatePartsFilters(
						{
							[FILTER_CONFIG_NAMES.TAGS]: ids.join(',') || undefined,
						},
						PARTS_TC
					);
				},
				component: OWAsyncSelect,
			},
		],
		[
			updatePartsFilters,
			vendors,
			fetchVendors,
			fetchMultipleVendors,
			tags,
			fetchTags,
			fetchMultipleTags,
		]
	);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${PARTS_TC}.${propName}`, parts, {}),
		[parts]
	);

	const refreshParts = useCallback(() => {
		fetchParts(
			{},
			PARTS_TC,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchParts, getStateSliceProp]);

	const vendorOptions = useMemo(
		() =>
			allVendors.map((_) => ({
				label: `${_.name}${_.vendorId ? ` - ${_.vendorId}` : ''}`,
				value: _.id,
				alternativeNames: [_.name, ...(_.vendorId ? [_.vendorId] : [])],
			})),
		[allVendors]
	);

	const fetchAndSetAllVendors = useCallback(() => {
		return new Promise((resolve, reject) => {
			fetchVendors()
				.then((res) => {
					setAllVendors(res);
					resolve(true);
				})
				.catch(reject);
		});
	}, [fetchVendors]);

	const onUploadClick = useCallback(() => {
		setDownloading(true);
		Promise.all([fetchAllParts(), fetchAndSetAllVendors()])
			.then((res) => {
				setPartsForUpload(res[0]);
				setUploadPopupVisible(true);
			})
			.catch(message.error)
			.finally(() => setDownloading(false));
	}, [fetchAllParts, fetchAndSetAllVendors]);

	const onPartialUploadClick = useCallback(() => {
		setDownloading(true);
		fetchAndSetAllVendors()
			.then(() => setPartialUploadPopupVisible(true))
			.catch(message.error)
			.finally(() => setDownloading(false));
	}, [fetchAndSetAllVendors]);

	const glCodeEnabled = useMemo(() => isGLCodeAllowedBySettings(companyConfig), [companyConfig]);

	const headerDefs = useMemo(
		() => PART_MASTER_FLATFILE_HEADER([userCurrency], vendorOptions, glCodeEnabled),
		[userCurrency, vendorOptions, glCodeEnabled]
	);

	const exportAsCsv = useCallback(
		(parts) => {
			const date = moment().format(DATE_FORMAT);
			const csvTitle = `Parts_Master_List_${date}`;
			const csvExporter = new ExportToCsv({
				...CSV_EXPORT_DEFAULTS,
				filename: csvTitle,
				title: csvTitle,
				headers: headerDefs.map((h) => h.label),
			});
			const data =
				parts && parts.length > 0
					? parts.map((_) => getFlatfileRecordForPart(_, userCurrency, headerDefs))
					: [getFlatfileRecordForPart({}, userCurrency, headerDefs)];
			csvExporter.generateCsv(data);
		},
		[glCodeEnabled, userCurrency, vendorOptions]
	);

	const onDownloadSample = useCallback(() => {
		exportAsCsv(partsForUpload);
	}, [exportAsCsv, partsForUpload]);

	const downloadCSV = useCallback(() => {
		setExportingCsv(true);
		const filters = parts[PARTS_TC].filters;
		fetchAllParts(filters)
			.then((res) => {
				setPartsForUpload(res);
				exportAsCsv(res);
			})
			.finally(() => setExportingCsv(false));
	}, [exportAsCsv, fetchAllParts, parts]);

	const getImagesFromExisitngPart = useCallback(
		(id) => {
			const part = partsForUpload.find((_) => `${_.id}` === `${id}`);
			return nullSafeGetOrElse('images', part, []);
		},
		[partsForUpload]
	);

	const onDataUpload = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				const missingNumbers = getMissngPartNumbers(partsForUpload, results.validData);

				if (missingNumbers.length > 0) {
					reject(
						`Some parts are missing from existing list. Please include them! Part # ${missingNumbers.join(
							', '
						)} `
					);
				} else {
					setBulkUploadInProgress(true);
					setBulkUploadInitiated(true);
					createBulk(
						results.validData.map((_) => ({
							..._,
							id: _.id ? parseInt(_.id) : undefined,
							cost: _.cost ? parseFloat(_.cost) : undefined,
							images: getImagesFromExisitngPart(_.id),
						}))
					)
						.then((res) => {
							resolve('Updated successfully!');
							setTimeout(() => {
								_hideUploadPopup();
							}, 0);
						})
						.catch((err) => {
							reject(err || 'Unable to upload! Please contact support!');
							setBulkUploadInitiated(false);
						})
						.finally(() => setBulkUploadInProgress(false));
				}
			});
		},
		[createBulk, getImagesFromExisitngPart, _hideUploadPopup, partsForUpload]
	);

	const onPartialDataUpload = useCallback(
		(results) => {
			return new Promise((resolve, reject) => {
				const entities = results.validData.map((_) => ({
					..._,
					id: _.id ? parseInt(_.id) : undefined,
					cost: _.cost ? parseFloat(_.cost) : undefined,
				}));

				setBulkUploadInProgress(true);
				setBulkUploadInitiated(true);
				uploadPartialBulk(entities)
					.then((res) => {
						resolve('Upload initiated successfully!');
						setTimeout(() => {
							_hidePartialUploadPopup();
						}, 0);
					})
					.catch((err) => {
						reject(err || 'Unable to upload! Please contact support!');
						setBulkUploadInitiated(false);
					})
					.finally(() => setBulkUploadInProgress(false));
			});
		},
		[uploadPartialBulk]
	);

	const uploadMenu = (
		<Menu>
			<Menu.Item onClick={onUploadClick}>All Parts</Menu.Item>
			<Menu.Item onClick={onPartialUploadClick}>Partial Upload</Menu.Item>
		</Menu>
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={PARTS_TC}
				updateFilters={updatePartsFilters}
				stateSlice={parts}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="parts"
				tableColumns={columns}
				onTableRow={onRow}
				fetchData={partsRestCrudThunksForSupplier.read}
				initialSorters={DEFAULT_NAME_SORTER}
				preAppliedFilters={{}}
				initialFilters={{ isActive: true }}
				rightActions={
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
						<div className="flex flex-row items-center">
							<TagCreation targetCollectionName={TagEntityType.PART} />
							{bulkUploadInProgress ? null : (
								<OWPoll
									key="parts-upload"
									id="parts-upload"
									fetchStatusPromises={[getBulkUploadStatus]}
									successMessage="Parts Upload Completed!"
									message="Parts Upload in progress!"
									onRefresh={refreshParts}
									successMessageMandatory={bulkUploadInitiated}
									renderElementWithProps={(props) => (
										<Dropdown overlay={uploadMenu} {...props}>
											<Button
												size="large"
												className="inline-block-visible-md ml-2"
												icon={<UploadOutlined />}
												loading={downloading}
											>
												<span className="inline-block-visible-xxl">Upload</span>
											</Button>
										</Dropdown>
									)}
								/>
							)}

							<Popover content="Export CSV" trigger="hover">
								<Button
									size="large"
									icon={<DownloadOutlined />}
									className="inline-block-visible-md ml-2"
									onClick={downloadCSV}
									loading={exportingCsv}
								>
									<span className="inline-block-visible-xxl">Export CSV</span>
								</Button>
							</Popover>
							<Button
								className="ml-2"
								type="primary"
								size="large"
								onClick={() => history.push('/supplier/parts/overview/new')}
							>
								New Part
							</Button>
						</div>
					</AccessPermissionChecker>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
								alt="Add your first part"
							/>
						}
						headline={"Nothing's here!"}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any parts yet.
								</div>
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS}>
									<Button
										type="primary"
										onClick={() => history.push('/supplier/parts/overview/new')}
									>
										Add your first part
									</Button>
								</AccessPermissionChecker>
							</div>
						}
					/>
				}
			/>
			{uploadPopupVisible && (
				<UploadWithSampleModal
					type="Parts"
					title="Upload Parts"
					downloadText="Do you want to download the existing Parts?"
					sampleFileText="Download Parts"
					onCancel={_hideUploadPopup}
					onDownloadSample={onDownloadSample}
					fields={PART_MASTER_FLATFILE_HEADER([userCurrency], vendorOptions, glCodeEnabled)}
					onDataLoad={onDataUpload}
					showWarningMessage
				/>
			)}
			{partialUploadPopupVisible && (
				<PartialPartsUpload
					onDataUpload={onPartialDataUpload}
					hideUploadPopup={_hidePartialUploadPopup}
					onUploadSuccess={refreshParts}
					vendorOptions={vendorOptions}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	parts: state.parts,
	currentUser: state.session.currentUser,
	vendors: state.vendors,
	tags: state.tags,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchParts: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			partsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	updatePartsFilters: (filters, targetCollection) =>
		dispatch(PARTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(PARTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	fetchAllParts: (params) => dispatch(fetchAllPartsForSupplier(params)),
	createBulk: (entities) => dispatch(partsRestCrudThunksForSupplier.createBulk(entities)),
	fetchVendors: (params, targetCollectionName) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName
			)
		),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchTags: (params, targetCollectionName) =>
		dispatch(
			tagsOnPartsRestCrudThunksForSupplier.read(
				{ ...params, no_pagination: true },
				targetCollectionName
			)
		),
	fetchMultipleTags: (ids, targetCollectionName) =>
		dispatch(tagsOnPartsRestCrudThunksForSupplier.read({ ids }, targetCollectionName)),
	getBulkUploadStatus: () => dispatch(getPartialPartsUploadBulkStatus(ROLE_TYPES.SUPPLIER)()),
	uploadPartialBulk: (entities) => dispatch(uploadPartialPartsBulk(ROLE_TYPES.SUPPLIER)(entities)),
});

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(PartsIndexPage));
